'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import productService from '@/app/[locale]/_service/product.service'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import { getProductQueryParams } from '../server-utils'
import type { IProduct } from '@/app/[locale]/_types'

interface UseVisibleProductsQueryOptions {
  categoryId: string | undefined
  subcategoryId?: string
  shipperId: string | undefined
  platformId: string
  effectiveMenuId: string | undefined
  deliveryType: string
  isSeperatePage: boolean
  childCategoryId?: string
  source_id?: string
  /** Controls whether the query should execute */
  enabled: boolean
}

interface ProductsQueryResult {
  products: IProduct[]
  count: number
}

const PRODUCTS_PER_PAGE = 50

/**
 * Hook for fetching products with visibility-based loading.
 * Only fetches when enabled (i.e., when the section is visible).
 */
export function useVisibleProductsQuery({
  categoryId,
  subcategoryId,
  shipperId,
  platformId,
  effectiveMenuId,
  deliveryType,
  isSeperatePage,
  childCategoryId,
  source_id,
  enabled,
}: UseVisibleProductsQueryOptions) {
  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()

  return useInfiniteQuery({
    queryKey: [
      'section-products',
      shipperId,
      effectiveMenuId,
      deliveryType,
      categoryId,
      subcategoryId,
      source_id,
      dynamicLanguageEnabled,
    ],
    queryFn: async ({
      pageParam = 1,
    }): Promise<{
      products: IProduct[]
      count: number
      pageParam: number
    }> => {
      const params = await getProductQueryParams(pageParam, {
        limit: PRODUCTS_PER_PAGE,
        effectiveMenuId,
        deliveryType,
        category_id: subcategoryId || categoryId,
        aggregator_id: source_id,
      })

      const response = await productService.getAll(
        params,
        shipperId,
        platformId,
        dynamicLanguageEnabled
      )

      // Fallback to first child category if parent has no products
      if (
        isSeperatePage &&
        pageParam === 1 &&
        !response?.data?.products?.length &&
        childCategoryId
      ) {
        const fallbackParams = await getProductQueryParams(pageParam, {
          limit: PRODUCTS_PER_PAGE,
          effectiveMenuId,
          deliveryType,
          category_id: childCategoryId,
          aggregator_id: source_id,
        })

        const fallbackResponse = await productService.getAll(
          fallbackParams,
          shipperId,
          platformId,
          dynamicLanguageEnabled
        )

        return {
          products: fallbackResponse?.data?.products || [],
          count: fallbackResponse?.data?.count || 0,
          pageParam,
        }
      }

      return {
        products: response?.data?.products || [],
        count: response?.data?.count || 0,
        pageParam,
      }
    },
    // Only run query when enabled (visible) AND has a category ID
    enabled: enabled && isShipperResolved && Boolean(categoryId),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages): number | undefined => {
      const loadedProductCount = allPages.reduce(
        (total, page) => total + page.products.length,
        0
      )
      const totalPages = Math.ceil(lastPage.count / PRODUCTS_PER_PAGE)

      return loadedProductCount < lastPage.count && allPages.length < totalPages
        ? allPages.length + 1
        : undefined
    },
    select: (data): ProductsQueryResult => ({
      products: data.pages.flatMap((page) => page.products) as IProduct[],
      count: data.pages[0]?.count || 0,
    }),
    // Keep data fresh but don't refetch on every focus
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (keep in cache longer)
    refetchOnWindowFocus: false,
  })
}

export default useVisibleProductsQuery
